/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import * as appStrings from '@touchplan/strings';
import { Injectable, inject } from '@angular/core';
import { fancyDatabase } from '@touchplan/client-firebase';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { DefaultLoggerService } from '@touchplan/logging';
import { Observable, map, last, tap, catchError, forkJoin, of } from 'rxjs';
import { ImportTicketsService, IBSApiRequestResults } from './import-tickets.service';

export interface FileItem {
	name: string;
	startDate: string;
	thumbnailUrl: string;
	error: string | null;
	originalFile: File;
	order: number;
	id?: string;
}

@Injectable()
export class ImageCollectionService {
	private logger = inject(DefaultLoggerService);
	private ticketImportService = inject(ImportTicketsService);

	/**
	 * Creates a `FileItem` object from the provided file, order, and plan start date.
	 *
	 * @param file - The file to be wrapped in a `FileItem`.
	 * @param order - The order index of the file in the collection.
	 * @param planStartDate - The planned start date associated with the file.
	 * @returns A `FileItem` object containing metadata and a thumbnail URL for the file.
	 */
	public createFileItem(file: File, order: number, planStartDate: string): FileItem {
		return {
			order,
			name: file.name,
			startDate: planStartDate,
			thumbnailUrl: URL.createObjectURL(file),
			error: null,
			originalFile: file,
		};
	}

	/**
	 * Listens for updates of a specific job node by its ID.
	 *
	 * Subscribes to the node updates at the path corresponding to the given job ID,
	 * specifically under the 'results' child node.
	 *
	 * @param jobId - The unique identifier of the job to listen for updates.
	 * @returns An Observable that emits the latest `IBSApiRequestResults` for the specified job.
	 */
	public listenForStatus(jobId: string): Observable<IBSApiRequestResults> {
		const bsPath = fancyFirebaseGetters.getApiRequestsPath(jobId);
		const ref = fancyFirebaseReferencer.getRef(bsPath);
		const childRef = fancyDatabase.child(ref, 'results');
		return fancyDatabase.object$<IBSApiRequestResults>(childRef).pipe(map((value) => value.val()));
	}

	/**
	 * Uploads an array of files (images) to the java server for a specific project and job.
	 *
	 * Each file is uploaded using the `ticketImportService.uploadBSImage` method. The method updates the `id` property of each `FileItem`
	 * with the returned `pushId` from the server response (the `pushId` is basically the new rtdb id).
	 * If an error occurs during the upload of a file, the error is logged and the `error` property of the corresponding `FileItem` is set with an appropriate message.
	 *
	 * @param files - The array of `FileItem` objects to be uploaded.
	 * @param projectId - The ID of the project.
	 * @param jobId - The ID of the job associated with the current process.
	 * @returns An `Observable` that emits the array of `FileItem` objects after all uploads have completed.
	 */
	public uploadFiles(files: FileItem[], projectId: string, jobId: string): Observable<FileItem[]> {
		const requests = files.map((file, idx) =>
			this.ticketImportService.uploadBSImage(file.originalFile, projectId, jobId, file.order).pipe(
				last(),
				tap((response) => {
					if (response?.pushId) {
						file.id = response.pushId;
					}
				}),
				catchError((error) => {
					this.logger.error('Error uploading file:', file.name, error);
					file.error = error?.message || appStrings.IMAGE_COLLECTION_DEFAULT_UPLOAD_ERROR;
					return of(null); // Continue processing other files even if one fails
				})
			)
		);
		return forkJoin(requests).pipe(map(() => files));
	}

	/**
	 * Assigns an `order` property to each `FileItem` in the provided array based on its index.
	 *
	 * @param files - An array of `FileItem` objects to be ordered.
	 * @returns A new array of `FileItem` objects, each with an added `order` property corresponding to its index in the array.
	 */
	public orderFiles(files: FileItem[]): FileItem[] {
		return files.map((file, index) => ({
			...file,
			order: index,
		}));
	}
}
